// API 호출 단일 진입점.
// 모든 서버 호출은 본 모듈을 거치며 (a) 베이스 URL, (b) Authorization 헤더, (c) 에러 응답 정규화, (d) 401 자동 refresh + 재시도 를 통일한다.

import { useAuthStore } from "@/stores/authStore.ts";

const API_BASE = import.meta.env.VITE_API_BASE;

// 서버 표준 에러 응답을 클라이언트 예외로 정규화한다 (docs/api-spec.md §2).
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

interface ApiRequestInit extends Omit<RequestInit, "body" | "headers"> {
  body?: unknown;
  headers?: Record<string, string>;
  // 인증 헤더 강제 부착 여부 (기본 true). 로그인 전 라우트는 false 로 둘 수 있다.
  withAuth?: boolean;
  // 401 시 자동 refresh + 재시도 여부 (기본 true). /auth/refresh 자체에서는 false 로 둬 무한 루프를 막는다.
  autoRefresh?: boolean;
}

// 동시 다발 401 요청들이 각자 refresh 를 호출해 thundering herd 가 되지 않도록 단일 promise 를 공유한다.
let refreshInFlight: Promise<boolean> | null = null;

// 리프레시 토큰으로 새 액세스를 받아 store 에 반영한다.
// 성공 시 true, 실패(토큰 없음/만료/revoked) 시 store 를 비우고 false.
async function ensureRefreshedOnce(): Promise<boolean> {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  refreshInFlight = (async () => {
    const store = useAuthStore.getState();
    const session = store.session;
    if (!session?.refreshToken) {
      store.clearSession();
      return false;
    }
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ refreshToken: session.refreshToken }),
      });
      if (!res.ok) {
        // 서버가 "토큰 무효(401/403)"라고 명확히 답한 경우에만 로그아웃한다.
        // 5xx/게이트웨이 오류 같은 일시 장애로 세션을 지우면 멀쩡한 로그인이 풀린다(실제 발생 사례).
        if (res.status === 401 || res.status === 403) {
          store.clearSession();
        }
        return false;
      }
      // 서버가 리프레시 토큰을 회전(rotation)하면 새 refreshToken 도 함께 내려준다.
      // 이를 저장해야 다음 갱신에서 폐기된 토큰을 쓰지 않고, 슬라이딩 만료로 로그인이 유지된다(개선항목 1).
      const data = (await res.json()) as {
        accessToken: string;
        expiresAtSec: number;
        refreshToken?: string;
      };
      store.setSession({
        ...session,
        accessToken: data.accessToken,
        expiresAtSec: data.expiresAtSec,
        ...(data.refreshToken ? { refreshToken: data.refreshToken } : {}),
      });
      return true;
    } catch {
      // 네트워크 오류(오프라인/앱 시작 직후 연결 전 등)는 토큰 무효가 아니다. 세션을 유지하고
      // 이번 요청만 실패시킨다(다음 요청에서 자동 재시도). 여기서 지우면 아침 첫 실행마다 로그아웃될 수 있다.
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

// 단일 요청 실행. apiRequest 가 자동 재시도를 위해 본 함수를 최대 2번 호출한다.
async function executeOnce(
  path: string,
  init: ApiRequestInit | undefined,
  withAuth: boolean,
): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(init?.headers ?? {}),
  };
  if (init?.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (withAuth) {
    const token = useAuthStore.getState().session?.accessToken;
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }
  return fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
}

// 단일 진입 fetch 래퍼.
// 응답이 표준 envelope({ ok, code, message }) 형태가 아닐 경우에도 안전하게 ApiError 로 변환한다.
// 401(AUTH_REQUIRED/AUTH_INVALID) 응답을 받으면 한 번에 한해 자동으로 refresh 후 재시도한다.
export async function apiRequest<T>(path: string, init?: ApiRequestInit): Promise<T> {
  const withAuth = init?.withAuth ?? true;
  const autoRefresh = init?.autoRefresh ?? true;

  let res = await executeOnce(path, init, withAuth);

  if (res.status === 401 && withAuth && autoRefresh) {
    const refreshed = await ensureRefreshedOnce();
    if (refreshed) {
      res = await executeOnce(path, init, withAuth);
    }
  }

  // 204 No Content 처리.
  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  let payload: unknown = null;
  if (text.length > 0) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { ok: false, code: "INTERNAL", message: text.slice(0, 200) };
    }
  }

  if (!res.ok) {
    const obj = (payload ?? {}) as { code?: string; message?: string };
    throw new ApiError(
      res.status,
      obj.code ?? "INTERNAL",
      obj.message ?? "요청 처리에 실패했습니다.",
    );
  }
  return payload as T;
}

// 자주 쓰이는 단축형. 의도 명확성을 위해 method 별 분기를 노출.
export const api = {
  get<T>(path: string, init?: Omit<ApiRequestInit, "method" | "body">) {
    return apiRequest<T>(path, { ...init, method: "GET" });
  },
  post<T>(path: string, body?: unknown, init?: Omit<ApiRequestInit, "method" | "body">) {
    return apiRequest<T>(path, { ...init, method: "POST", body });
  },
  patch<T>(path: string, body?: unknown, init?: Omit<ApiRequestInit, "method" | "body">) {
    return apiRequest<T>(path, { ...init, method: "PATCH", body });
  },
  delete<T>(path: string, init?: Omit<ApiRequestInit, "method" | "body">) {
    return apiRequest<T>(path, { ...init, method: "DELETE" });
  },
};
